import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";

/** Edit and delete for any register row, driven by the record schema the API publishes. */
export const RecordActions = ({ resource, record, onDone }) => {
  const [schema, setSchema] = useState(null);
  const [open, setOpen] = useState("");
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!resource) return;
    try {
      const res = await api.get(`/records/${resource}/schema`);
      setSchema(res.data);
    } catch { setSchema(false); }
  }, [resource]);

  useEffect(() => { load(); }, [load]);

  if (!resource || !schema || !schema.may_write || !record?.id) return null;

  const startEdit = () => {
    const next = {};
    schema.fields.forEach((f) => { next[f.key] = record[f.key] ?? (f.type === "switch" ? false : ""); });
    setForm(next);
    setOpen("edit");
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/records/${resource}/${record.id}`, { values: form });
      toast.success(res.data.message);
      setOpen("");
      await onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/records/${resource}/${record.id}`,
                                   { params: { reason } });
      toast.success(res.data.message);
      setOpen("");
      setReason("");
      await onDone?.(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={`record-actions-${resource}`}>
      <Button size="sm" variant="outline" onClick={startEdit}
              data-testid="record-edit-button"
              className="h-7 rounded-full border-border px-2.5 text-[10px]">
        <Pencil className="mr-1 h-3 w-3" /> Edit
      </Button>
      {schema.deletable && (
        <Button size="sm" variant="outline" onClick={() => setOpen("delete")}
                data-testid="record-delete-button"
                className="h-7 rounded-full border-border px-2.5 text-[10px] text-red-600 hover:text-red-700 dark:text-red-400">
          <Trash2 className="mr-1 h-3 w-3" /> Delete
        </Button>
      )}

      <Dialog open={open === "edit"} onOpenChange={(o) => !o && setOpen("")}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit this {schema.label.toLowerCase()}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {schema.fields.map((f) => (
              <div key={f.key}
                   className={`space-y-1.5 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
                <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
                {f.type === "select" ? (
                  <Select value={String(form[f.key] ?? "")}
                          onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`record-field-${f.key}`} className="bg-muted/40">
                      <SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>))}
                    </SelectContent>
                  </Select>
                ) : f.type === "switch" ? (
                  <div className="flex h-9 items-center">
                    <Switch checked={!!form[f.key]} data-testid={`record-field-${f.key}`}
                            onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} />
                  </div>
                ) : f.type === "date" ? (
                  <DateField value={String(form[f.key] || "").slice(0, 10)}
                             testId={`record-field-${f.key}`}
                             onChange={(v) => setForm({ ...form, [f.key]: v })} />
                ) : f.type === "textarea" ? (
                  <Textarea rows={3} value={form[f.key] ?? ""}
                            data-testid={`record-field-${f.key}`}
                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                            className="bg-muted/40 text-[12.5px]" />
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""}
                         data-testid={`record-field-${f.key}`}
                         onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                         className="bg-muted/40 text-[12.5px]" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={busy} data-testid="record-edit-save"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open === "delete"} onOpenChange={(o) => !o && setOpen("")}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this {schema.label.toLowerCase()}?</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">
            The record leaves the register and is archived with your name and reason, so it can be
            recovered if this was a mistake. Anything that depends on it will block the delete.
          </p>
          <Textarea rows={2} value={reason} data-testid="record-delete-reason"
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this being deleted?"
                    className="bg-muted/40 text-[12.5px]" />
          {reason.trim().length > 0 && reason.trim().length < 5 && (
            <p className="text-[11px] text-red-600 dark:text-red-400"
               data-testid="record-delete-reason-error">
              Give at least 5 characters so the archive says why.
            </p>
          )}
          <DialogFooter>
            <Button onClick={remove} disabled={busy || reason.trim().length < 5}
                    data-testid="record-delete-confirm"
                    className="rounded-full bg-red-600 text-white hover:bg-red-700">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete and archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
